import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  DatatableColumn,
  DatatableColumnType,
  SearchControl,
  SearchControlType,
  StringMap,
} from 'src/app/@theme/datatable/datatable.component';
import { RankListResponseWrapper } from 'src/app/api/models';

export interface RanklistRow {
  teamName: string;
  time?: number;
  rank: string;
  month: string;
  roomName: string;
  level: string;
  levelName: string;
  picture: string;
  room: string;
}

@Component({
  selector: 'app-ranklist',
  templateUrl: './ranklist.component.html',
  styleUrls: [
    './ranklist.component.scss',
    '../../../../ngx-datatable-dark.scss',
    '../../../../ngx-datatable-light.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class RanklistComponent {
  ranklist: RankListResponseWrapper[] = [];
  ranklistRows: RanklistRow[] = [];
  searchControlSettings: SearchControl[] = [];
  rooms: StringMap = {};
  roomId?: string;

  readonly levels = {
    EASY: 'Easy',
    MEDIUM: 'Medium',
    HARD: 'Hard',
    X_HARD: 'Master',
    ONLINE: 'Online',
  };

  @Input() loading: boolean = false;
  @Input() filters: StringMap | undefined;
  @Input() theme: string = '';

  @Input() branches: StringMap | undefined;
  @Input() namespaces: StringMap | undefined;
  @Input() namespace: string | undefined;

  @Input('roomId') set setRoomId(roomId: string | undefined) {
    this.roomId = roomId;
  }

  @Output() filterChange = new EventEmitter<any>();

  @HostBinding('class.light-theme') get isLightTheme() {
    return this.theme === 'light';
  }
  @HostBinding('class.dark-theme') get isDarkTheme() {
    return this.theme === 'dark';
  }

  columns: DatatableColumn[] = [
    {
      prop: 'teamName',
      name: 'Team name',
      sortable: true,
      width: 130,
    },
    {
      prop: 'time',
      name: 'Time',
      sortable: true,
      width: 80,
      type: DatatableColumnType.TIME,
    } as DatatableColumn,
    {
      prop: 'rank',
      name: 'Rank',
      sortable: true,
      width: 60,
    },
    {
      prop: 'month',
      name: 'Month',
      sortable: false,
      width: 60,
    },
    {
      prop: 'roomName',
      name: 'Room',
      sortable: false,
      width: 130,
    },
    {
      prop: 'levelName',
      name: 'Level',
      sortable: false,
      width: 60,
    },
    {
      prop: 'picture',
      name: 'Picture',
      sortable: false,
      width: 360,
      type: DatatableColumnType.IMAGE,
    } as DatatableColumn,
  ];

  private buildSearchControlSettings(): void {
    if (!this.ranklist) {
      return;
    }

    const today = new Date();
    const originalToday = new Date(today);
    const months: StringMap = {};

    for (let i = 0; i < 4; i++) {
      const dateKey =
        today.getFullYear() +
        '.' +
        String(today.getMonth() + 1).padStart(2, '0') +
        '.';
      const dateValue = today.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
      });

      months[dateKey] = dateValue;
      today.setMonth(today.getMonth() - 1);
    }

    const defaultMonth =
      originalToday.getFullYear() +
      '.' +
      String(originalToday.getMonth() + 1).padStart(2, '0') +
      '.';

    this.searchControlSettings = [
      {
        title: 'Branch',
        type: SearchControlType.SELECT,
        options: this.namespaces,
        property: 'branch',
        defaultValue: this.namespace,
      },
      {
        title: 'Month',
        type: SearchControlType.SELECT,
        property: 'month',
        options: months,
        defaultValue: defaultMonth,
      },
      {
        title: 'Room',
        type: SearchControlType.SELECT,
        property: 'room',
        options: this.rooms,
        defaultValue: this.roomId,
      },
      {
        title: 'Level',
        type: SearchControlType.SELECT,
        property: 'level',
        options: this.levels,
        defaultValue: 'MEDIUM',
      },
    ];
  }

  @Input('rooms')
  set setRooms(rooms: StringMap) {
    this.rooms = rooms;
    this.buildSearchControlSettings();
    this.mapRanklistRows(this.ranklist);
  }

  @Input('ranklist')
  set setRanklist(ranklist: RankListResponseWrapper[]) {
    this.ranklist = ranklist;
    this.buildSearchControlSettings();
    this.mapRanklistRows(ranklist);
  }

  private mapRanklistRows(ranklist: RankListResponseWrapper[]): void {
    if (ranklist) {
      const data = ranklist.map((games) => games.data);

      this.ranklistRows = data.flatMap((items) => {
        return items!.map((item) => {
          const roomName = item.room!.replace(/ \d+$/, '');

          return {
            teamName: item.teamName ? item.teamName : '',
            time: item.time ? item.time : '',
            rank: item.rank ? item.rank.toString() : '',
            month: item.month ? item.month : '',
            roomName: roomName,
            room: this.roomId,
            level: item.level ? item.level : '',
            levelName:
              item.level && item.level === 'X_HARD'
                ? 'Master'
                : item.level
                  ? item.level.charAt(0) + item.level.slice(1).toLowerCase()
                  : '',
            picture: item.picture
              ? item.picture
              : '../../../../assets/noteampicture.png',
          } as RanklistRow;
        });
      });
    }
  }

  onFilterChange(event: any): void {
    this.filterChange.emit(event);
  }
}
